import React, { memo, useCallback } from 'react';
import { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd';
import { TFunction } from 'i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import _get from 'lodash/get';
import classNames from 'classnames';
import { FieldComponents, FieldItem, Values } from '@lib/interfaces/form';
import removeIdFields from '@lib/utils/removeIdsFromObject';
import getLastPosition from '@lib/utils/getLastPosition';
import FormItem from './FormItem';
import FieldArrayItemAccordion from './FieldArrayItemAccordion';
import getFieldArrayProps from './getFieldArrayProps';
import { FieldArrayItemType } from './types';

import SubFieldsArray from './FieldArray'; // TODO: fix it
import styles from './Form.module.scss';

export interface ItemProps {
  append: (v: Values) => void;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  emptyStateText?: string;
  emptyStateTitle?: string;
  entity?: string;
  fieldComponents?: FieldComponents;
  formId?: string;
  formItemsClassName?: string;
  groupIndex?: number;
  id?: string;
  index: number;
  isDraggable?: boolean;
  isFocused?: boolean;
  isSubItemDisabled?: boolean;
  isSubItemDraggable?: boolean;
  isSubItemRemovable?: boolean;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  onNextFocus?: () => void;
  onPrevFocus?: () => void;
  parentName: string;
  remove: (index: number) => void;
  sourceAppendValues?: { [key: string]: unknown };
  subFields: FieldItem[];
  subFieldsMaxLength?: number;
  subFieldsMinLength?: number;
  t: TFunction<'translation', undefined>;
  title?: string;
}

function FieldArrayItem(props: ItemProps) {
  const {
    append,
    dragHandleProps,
    fieldComponents,
    formId,
    formItemsClassName,
    groupIndex,
    id,
    index,
    isDraggable,
    isFocused,
    isSubItemDisabled = false,
    isSubItemDraggable = true,
    isSubItemRemovable = true,
    onClick,
    onNextFocus,
    onPrevFocus,
    parentName,
    remove,
    sourceAppendValues,
    subFields,
    subFieldsMaxLength,
    subFieldsMinLength,
    title,
  } = props;
  const { formState, getValues, resetField, setValue } = useFormContext();
  const errors = _get(formState, ['errors', parentName, index]);
  const errorsCount = errors ? Object.keys(errors).length : 0;
  // Subscribe to this array only (each sub-field has its own subscription),
  // so the item re-renders when the list changes, not on every keystroke
  // elsewhere. The data itself is read synchronously from getValues: the
  // watched value can trail useFieldArray by a render right after a remove.
  useWatch({ name: parentName });
  const readParentValue = useCallback(
    () => (getValues(parentName) ?? []) as FieldArrayItemType[],
    [getValues, parentName],
  );
  const parentValue = readParentValue();
  const value = parentValue?.[index];

  const sourceAppendValue = sourceAppendValues?.[parentName] as
    Values | undefined;

  const onAdd = useCallback(
    (appendValue: Values, appendPosition?: number) => {
      const currentParentValue = readParentValue();
      const lastPosition = getLastPosition(currentParentValue);
      // Copy: appendValue is often a shared template (sourceAppendValues),
      // and writing `position` onto it would leak into the next append.
      const preparedAppendValue: Values | undefined = appendValue
        ? { ...appendValue }
        : undefined;

      if (preparedAppendValue && !preparedAppendValue.position) {
        preparedAppendValue.position = lastPosition + 1;
      }

      if (typeof appendPosition === 'number' && appendPosition >= 0) {
        const newArray = [
          ...currentParentValue.slice(0, appendPosition),
          preparedAppendValue,
          ...currentParentValue.slice(appendPosition),
        ];
        setValue(parentName, newArray, {
          shouldDirty: true,
          shouldValidate: true,
        });
        if (onNextFocus) onNextFocus();
        return undefined;
      }

      return sourceAppendValue || preparedAppendValue
        ? append(preparedAppendValue as Values)
        : undefined;
    },
    [
      readParentValue,
      sourceAppendValue,
      append,
      setValue,
      parentName,
      onNextFocus,
    ],
  );

  const onDuplicate = useCallback(
    () => append(removeIdFields(value)),
    [append, value],
  );

  const onRemove = useCallback(() => {
    const nextParentValue = readParentValue().filter((_, idx) => idx !== index);
    remove(index);
    resetField(parentName, { defaultValue: nextParentValue });
    if (onPrevFocus) onPrevFocus();
  }, [index, onPrevFocus, parentName, readParentValue, remove, resetField]);

  const getSubFieldContent = () => (
    <div className={classNames(styles.formSubItems, formItemsClassName)}>
      {subFields.map((subField) => {
        const {
          name,
          validation,
          getLabel,
          subFields: subSubFields,
          componentProps,
          ...restFieldProps
        } = subField;
        const { label } = restFieldProps;
        let newLabel = label;
        const fieldName = `${parentName}.${index}.${name}`;
        if (getLabel) {
          newLabel = getLabel(getValues(), fieldName);
        }
        if (subSubFields !== undefined) {
          return (
            <SubFieldsArray
              key={`${fieldName}-${id}`}
              {...getFieldArrayProps(subField, fieldName)}
              formId={formId}
              sourceAppendValues={sourceAppendValues}
            />
          );
        }
        return (
          <FormItem
            {...restFieldProps}
            componentProps={{
              ...componentProps,
              disabled: isSubItemDisabled || componentProps?.disabled,
            }}
            key={`${fieldName}-${id}`}
            name={fieldName}
            label={newLabel}
            formId={formId}
            validation={validation}
          />
        );
      })}
    </div>
  );

  if (fieldComponents?.FieldArrayItemWrapperComponent) {
    const { FieldArrayItemWrapperComponent } = fieldComponents;
    return (
      <FieldArrayItemWrapperComponent
        dragHandleProps={dragHandleProps}
        errorsCount={errorsCount}
        isDraggable={isDraggable}
        isFocused={isFocused}
        isSubItemDisabled={isSubItemDisabled}
        isSubItemDraggable={isSubItemDraggable}
        isSubItemRemovable={isSubItemRemovable}
        onAdd={onAdd}
        onClick={onClick}
        onDuplicate={onDuplicate}
        onRemove={onRemove}
        parentGroupIndex={groupIndex}
        parentIndex={index}
        parentName={parentName}
        parentValue={parentValue}
        subFieldsMaxLength={subFieldsMaxLength}
        subFieldsMinLength={subFieldsMinLength}
        value={value}
      >
        {getSubFieldContent()}
      </FieldArrayItemWrapperComponent>
    );
  }

  return (
    <FieldArrayItemAccordion
      dragHandleProps={dragHandleProps}
      errorsCount={errorsCount}
      index={index}
      isDraggable={isDraggable}
      onRemove={() => remove(index)}
      parentName={parentName}
      title={`${title} ${index + 1}`}
    >
      {getSubFieldContent()}
    </FieldArrayItemAccordion>
  );
}

export default memo(FieldArrayItem);
